import { FC, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";

import { MULTI_ANALYSIS_MEASURE_TYPE } from "../../types";
import type { IMultiAnalysisTemplateCell } from "../../utils/analysis-template-settings";
import {
  buildTemplateCellFilters,
  EMPTY_TEMPLATE_CELL_FILTER_VALUES,
  isFrequencyRangeBoundsInvalid,
  MANUAL_TEMPLATE_PRESENTATION_OPTIONS,
  MANUAL_TEMPLATE_RANGE_TYPE_OPTIONS,
  MANUAL_TEMPLATE_UNIT_OPTIONS,
  normalizeFrequencyRangeParamInput,
  normalizeManualTemplateSelectValue,
  readTemplateCellFilterValues,
  supportsFrequencyRangeParams,
} from "../../utils/template-filters";

const MEASURE_TYPE_OPTIONS = [
  MULTI_ANALYSIS_MEASURE_TYPE.SPECTRUM,
  MULTI_ANALYSIS_MEASURE_TYPE.SIGNAL_FORM,
  MULTI_ANALYSIS_MEASURE_TYPE.ORBIT,
  MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND,
] as const;

interface IManualTemplateCellFiltersEditorProps {
  cell: IMultiAnalysisTemplateCell;
  onChange: (patch: Partial<Pick<IMultiAnalysisTemplateCell, "measureType" | "filters">>) => void;
}

export const ManualTemplateCellFiltersEditor: FC<IManualTemplateCellFiltersEditorProps> = ({ cell, onChange }) => {
  const { formatMessage } = useIntl();
  const filterValues = useMemo(() => readTemplateCellFilterValues(cell.filters), [cell.filters]);
  const rangeTypeValue = normalizeManualTemplateSelectValue(filterValues.rangeType);
  const unitsValue = normalizeManualTemplateSelectValue(filterValues.units);
  const presentationValue = normalizeManualTemplateSelectValue(filterValues.presentation);
  const frequencyRangeError = isFrequencyRangeBoundsInvalid(filterValues.param1, filterValues.param2);
  const frequencyRangeErrorMessage = frequencyRangeError
    ? formatMessage({ id: "pages.multiAnalysis.templateEditor.frequencyRangeInvalid" })
    : undefined;

  const updateFilters = (patch: Partial<typeof filterValues>, measureType = cell.measureType) => {
    onChange({
      measureType,
      filters: buildTemplateCellFilters({ ...filterValues, ...patch }),
    });
  };

  const handleFrequencyParamBlur = (field: "param1" | "param2") => () => {
    const normalized = normalizeFrequencyRangeParamInput(filterValues[field]);

    if (normalized !== filterValues[field]) {
      updateFilters({ [field]: normalized });
    }
  };

  return (
    <Stack spacing={1}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#3f6db5" }}>
        <FormattedMessage id="pages.multiAnalysis.templateEditor.sourceMeasureParams" />
      </Typography>
      <TextField
        select
        size="small"
        label={<FormattedMessage id="pages.multiAnalysis.templateEditor.displayType" />}
        value={cell.measureType}
        onChange={event => {
          const measureType = event.target.value as MULTI_ANALYSIS_MEASURE_TYPE;
          onChange({
            measureType,
            filters: buildTemplateCellFilters(EMPTY_TEMPLATE_CELL_FILTER_VALUES),
          });
        }}
      >
        {MEASURE_TYPE_OPTIONS.map(option => (
          <MenuItem key={option} value={option}>
            <FormattedMessage id={`pages.multiAnalysis.measureWindow.${option}`} />
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label={<FormattedMessage id="pages.multiAnalysis.templateEditor.range" />}
        value={rangeTypeValue}
        onChange={event => updateFilters({ rangeType: event.target.value })}
      >
        <MenuItem value="">
          <FormattedMessage id="pages.multiAnalysis.templateEditor.anyValue" />
        </MenuItem>
        {MANUAL_TEMPLATE_RANGE_TYPE_OPTIONS.map(option => (
          <MenuItem key={option} value={option}>
            {formatMessage({ id: `enums.measurementRangeType.${option}` })}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction="row" spacing={1}>
        <TextField
          select
          size="small"
          label={<FormattedMessage id="pages.multiAnalysis.templateEditor.units" />}
          value={unitsValue}
          onChange={event => updateFilters({ units: event.target.value })}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">
            <FormattedMessage id="pages.multiAnalysis.templateEditor.anyValue" />
          </MenuItem>
          {MANUAL_TEMPLATE_UNIT_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {formatMessage({ id: `enums.units.${option}` })}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={<FormattedMessage id="pages.multiAnalysis.templateEditor.presentation" />}
          value={presentationValue}
          onChange={event => updateFilters({ presentation: event.target.value })}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">
            <FormattedMessage id="pages.multiAnalysis.templateEditor.anyValue" />
          </MenuItem>
          {MANUAL_TEMPLATE_PRESENTATION_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {formatMessage({ id: `enums.unitsPresentation.${option}` })}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {supportsFrequencyRangeParams(cell.measureType) ? (
        <Box>
          <Typography sx={{ fontSize: 11, color: "#6a7284", mb: 0.5 }}>
            <FormattedMessage id="pages.multiAnalysis.templateEditor.frequencyRange" />
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              type="number"
              inputProps={{ step: 0.01 }}
              value={filterValues.param1}
              error={frequencyRangeError}
              helperText={frequencyRangeError ? frequencyRangeErrorMessage : undefined}
              onChange={event => updateFilters({ param1: event.target.value })}
              onBlur={handleFrequencyParamBlur("param1")}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type="number"
              inputProps={{ step: 0.01 }}
              value={filterValues.param2}
              error={frequencyRangeError}
              helperText={frequencyRangeError ? frequencyRangeErrorMessage : undefined}
              onChange={event => updateFilters({ param2: event.target.value })}
              onBlur={handleFrequencyParamBlur("param2")}
              sx={{ flex: 1 }}
            />
            <Typography sx={{ fontSize: 12, color: "#6a7284" }}>
              <FormattedMessage id="pages.multiAnalysis.templateEditor.hertz" />
            </Typography>
          </Stack>
        </Box>
      ) : null}
    </Stack>
  );
};
